import { getFieldDisplayName } from '~/assets/javascript/utils/table/field';
import type { DraftRecord } from '~/types/record';
import type { View } from '~/types/view';

export const SEARCHABLE_KEY = '_objectStringified';

export type GroupedRecords = {
  value: string;
  recordIds: string[];
  recordCount: number;
};

export default class RecordList {
  emptyLabel: string;
  options: any;
  searchTerm: any;
  selectedFacets: any;
  view: View;
  draftRecords: DraftRecord[];

  constructor({
    view,
    records,
    options,
    emptyLabel,
  }) {
    this.view = view;
    this.emptyLabel = emptyLabel;
    this.options = options;
    this.draftRecords = records;
  }

  findRecord(recordId) {
    if (this.hasToGroupRecords) {
      const group = this.findRecordGroupByRecordId(recordId);
      return group?.recordIds?.find(id => id === recordId);
    }

    return this.records.find(record => record.id === recordId);
  }

  /**
   * @returns {boolean}
   * @readonly
   * @description
   * Returns true if the view requires to group records by some field value.
   * Otherwise, returns false.
   */
  get hasToGroupRecords() {
    return this.options.hasGroup && this.view.page_type === 'Kanban';
  }

  get records(): GroupedRecords[] | DraftRecord[] {
    if (this.hasToGroupRecords) return this.groupRecords(this.draftRecords);

    return this.draftRecords;
  }

  get sortedIds() {
    return this.records.map(record => record.id);
  }

  get groupField() {
    return this.options.fieldIdMapping[this.options.groupFieldId];
  }

  get optionsGroups() {
    const optionsSource = (this.groupField?.type === 'Select'
      ? this.groupField?.options?.select_options
      : this.groupField?.linkedRecordValues) || [];

    if (_isEmpty(optionsSource)) return [];

    return optionsSource.map(option => ({
      ...option,
      recordIds: [],
      recordCount: 0,
    })) || [];
  }

  groupRecords(items): GroupedRecords[] {
    const emptyGroup = { value: this.emptyLabel, recordIds: [], recordCount: 0 };
    const groups = [emptyGroup, ...this.optionsGroups];

    items.forEach((record) => {
      const fieldData = record[this.options.groupFieldId];

      if (!fieldData) {
        groups[0].recordIds.push(record.id);
        groups[0].recordCount += 1;
        return;
      }

      const displayName = getFieldDisplayName(
        fieldData,
        this.options.fieldIdMapping[this.options.groupFieldId],
      );

      if (Array.isArray(displayName) && displayName.length === 0) {
        groups[0].recordIds.push(record.id);
        groups[0].recordCount += 1;
        return;
      }

      const group = groups.find(
        group => (Array.isArray(displayName) ? displayName.includes(group.value) : group.value === displayName),
      );

      if (!group) return;

      group.recordIds.push(record.id);
      group.recordCount += 1;
    });

    return groups;
  }

  findRecordGroupByRecordId(recordId) {
    return this.records.find(group => group.recordIds.some(id => id === recordId));
  }
}
